import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { Alert, Button, CircularProgress, Dialog, DialogContent, TextField, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { unwoundRowMetadataFromId } from 'interfaces/genericFields/unwindRowsWithInnerArrays';

import { DialogChromeHeader } from 'components/atoms/Dialog/DialogChrome';
import { useTableEditingContext } from 'components/atoms/EditableDataGrid/TableEditingContext';
import { useConfirmation } from 'components/modals/ConfirmationContext';
import ConfirmationModal from 'components/modals/ConfirmationModal';
import { has } from 'es-toolkit/compat';
import { Permission } from 'interfaces/permissionOption';
import { Procedure } from 'interfaces/procedure';
import { isProcedureFieldOptionsLoading, ProceduresFieldsContext } from 'interfaces/procedure/fields/helpers';
import { Slide } from 'interfaces/slide';
import { concat, filter, find, first, flatMap, includes, isEmpty, map, size, some } from 'lodash';
import React, { ChangeEvent, FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { DelimitedArrayParam, StringParam, useQueryParam } from 'use-query-params';
import { CompressedJsonParam } from 'utils/customQueryParamsConfigs';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { DEFAULT_PAGE_SIZE } from 'utils/useEncodedFilters/constants';
import { usePermissions } from 'utils/usePermissions';
import { useEditableFields } from '../ProceduresPage/DataGrids/useEditableFields';
import { canQueryPendingSlides } from '../ProceduresPage/pendingSlidesPermissions';
import { useAllPendingSlides } from '../ProceduresPage/usePendingSlides';
import {
  CASE_ORGANIZATION_WORKFLOW_LOADING_MESSAGE,
  getCaseOrganizationWorkflowMetrics,
  getStudyWorkflowInventory,
} from '../Workflow/studyWorkflowInventory';
import {
  CaseManifestEntryChoices,
  CaseManifestHandling,
  CaseManifestSlideSourceMenu,
  ManifestWorkspaceSurface,
} from './CaseManifestControls';
import {
  CaseManifestDialogFooter,
  CaseManifestPlanToolbar,
  CaseManifestSlideAdditionDialog,
} from './CaseManifestDialogChrome';
import { CaseManifestFileMenu, CaseManifestFileStatus } from './CaseManifestFileActions';
import { CaseManifestIngestNotice } from './CaseManifestIngestNotice';
import { CaseManifestTargetCaseCell } from './CaseManifestTargetCaseCell';
import { CaseManifestWorkspace } from './CaseManifestWorkspace';
import {
  buildCaseOrganizationPlan,
  CaseOrganizationExecutionMode,
  caseOrganizationHandlingLabels,
  caseOrganizationSubmissionLabels,
} from './caseOrganizationPlan';
import { caseNameFromFileName, updateManifestCaseTarget } from './caseTargetAssignment';
import { CaseOrganizationConfirmationDialog } from './ConfirmationDialog';
import {
  assignManifestDraftCaseIds,
  ManifestCaseTarget,
  ManifestDialogUrlState,
  ManifestDraftCase,
  ManifestSurface,
  ManifestSurfaceParam,
  normalizeManifestDialogState,
  SlideAdditionChoice,
  SlideAdditionOptions,
} from './dialogState';
import { buildDraftDuplicateGroups, keepChosenDraftRows } from './duplicateSlideRows';
import { DuplicateSlideRowsDialog } from './DuplicateSlideRowsDialog';
import { StudyManifestInProgressDialog } from './InProgressDialog';
import { resolveManifestDraftCases as resolveDraftCases, resolveManifestDraftAssignments } from './manifestDraft';
import { ManifestDraftStatus } from './ManifestDraftStatus';
import {
  addSavedCasesToDraft,
  applyManifestDraftPatches,
  collectCaseSharedMetadataErrors,
  collectDuplicateSlideRowErrors,
  collectExplicitNewCaseCollisionSlideIds,
  collectExplicitNewCaseLabelErrors,
  collectManifestCaseLabelErrors,
  collectManifestValidationErrors,
  getManifestCaseTargets,
  joinNewTargetsToUniqueExistingCases,
  removeSlidesFromManifestCases,
} from './manifestResolution';
import { projectManifestWorkbookPlan } from './manifestWorkbook';
import { useAllStudyProcedures } from './useAllStudyProcedures';
import { useCaseOrganizationExecution } from './useCaseOrganizationExecution';
import { useManifestFileImport } from './useManifestFileImport';
import { useManifestPlanEditing } from './useManifestPlanEditing';
import { useManifestSlideMetadata } from './useManifestSlideMetadata';
import { useManifestSourceSelection } from './useManifestSourceSelection';
import { useManifestWorkbookExport } from './useManifestWorkbookExport';

interface StudyManifestDialogProps {
  studyId: string;
  onClose: (event?: any) => void;
  onOpenStandard?: () => void;
  open: boolean;
}

const dialogTitle = 'Assign slides to cases';
const crossLabSearchSurface = 'Cross Lab Search';
type WorkspaceSurface = ManifestWorkspaceSurface;

const CaseManifestDialog: FunctionComponent<React.PropsWithChildren<StudyManifestDialogProps>> = ({
  studyId,
  onClose,
  open,
}) => {
  const confirmWithModal = useConfirmation();
  const { labId, currentLab } = useCurrentLabId();
  const { hasEveryPermission, hasPermission } = usePermissions();
  const canReadStudySource = canQueryPendingSlides({ hasPermission, studyId });
  const canReadLabSource = hasPermission(Permission.ViewPendingSlides);
  const canIncludeAssigned = hasPermission(Permission.ApplyCaseManifest);
  const canSwitchLabs = hasPermission(Permission.ApplyPermissionsAcrossLabs);
  const canAddSlidesAcrossLabs = hasEveryPermission([
    Permission.ApplyCaseManifest,
    Permission.ApplyPermissionsAcrossLabs,
  ]);
  const { fieldsContext: procedureFieldContext } = useTableEditingContext<Procedure, ProceduresFieldsContext, Slide>();

  const [manifestDialogUrlState, setManifestDialogUrlState] = useQueryParam<ManifestDialogUrlState | undefined>(
    'caseManifestState',
    CompressedJsonParam
  );
  const [caseManifestSelectionMode, setCaseManifestSelectionMode] = useQueryParam(
    'caseManifestSelectionMode',
    StringParam
  );
  const [, setCaseManifestSelectedRows] = useQueryParam('caseManifestSelectedRows', DelimitedArrayParam);
  const [, setCaseManifestOmittedRows] = useQueryParam('caseManifestOmittedRows', DelimitedArrayParam);
  const [manifestSurfaceState, setManifestSurface] = useQueryParam<ManifestSurface | undefined>(
    'manifestSurface',
    ManifestSurfaceParam
  );

  const manifestState = useMemo(() => normalizeManifestDialogState(manifestDialogUrlState), [manifestDialogUrlState]);
  const {
    draftCases = [],
    draftWarnings = [],
    operationErrors = [],
    manualManifestSlideIdsToLoad = [],
    manualManifestGroupIntoSingleCase = false,
    existingCaseAssignmentsBySlideId = {},
    manualManifestErrorMessages = [],
    removedSlideIds = [],
    casePatchesById = {},
    slidePatchesById = {},
    executionMode = 'routine',
    showOnlyErrors = false,
    showOnlyUnassignedSlides = false,
    useFileNameForNewCaseLabels = true,
    page = 1,
    rowsPerPage = DEFAULT_PAGE_SIZE,
    pendingSlidesSelectionAction,
    reviewExistingCasesWhenAvailable,
    reviewUnassignedSlidesWhenAvailable,
    includeStudyPendingSlidesWhenAvailable,
  } = manifestState;

  const isExecutionModeValid = has(caseOrganizationHandlingLabels, [executionMode]);
  const overrideCases = isExecutionModeValid && executionMode !== 'routine';

  const updateManifestDialogUrlState = useCallback(
    (
      updates: Partial<ManifestDialogUrlState> | ((previousState: ManifestDialogUrlState) => ManifestDialogUrlState)
    ) => {
      setManifestDialogUrlState((previousState) => {
        const currentState = normalizeManifestDialogState(previousState);
        return normalizeManifestDialogState(
          typeof updates === 'function' ? updates(currentState) : { ...currentState, ...updates }
        );
      }, 'replaceIn');
    },
    [setManifestDialogUrlState]
  );

  const draftUploadScope = JSON.stringify([
    draftCases,
    manualManifestSlideIdsToLoad,
    casePatchesById,
    slidePatchesById,
    removedSlideIds,
    existingCaseAssignmentsBySlideId,
  ]);
  const [caseOrganizationReviewOpen, setCaseOrganizationReviewOpen] = useState(false);
  const [slideAdditionDialogOpen, setSlideAdditionDialogOpen] = useState(false);
  const [selectedDirectSlides, setSelectedDirectSlides] = useState<Slide[]>([]);
  const [directSearchResetKey, setDirectSearchResetKey] = useState(0);
  const [hasChosenEntryIntent, setHasChosenEntryIntent] = useState(false);

  const [selectSlidesAnchorEl, setSelectSlidesAnchorEl] = useState<HTMLElement | null>(null);
  const [localWorkspaceSurface, setLocalWorkspaceSurfaceState] = useState<WorkspaceSurface | null>(null);
  // The search remounts empty, so selections made in it must not outlive the surface.
  const setLocalWorkspaceSurface = (surface: WorkspaceSurface | null) => {
    if (surface !== crossLabSearchSurface) setSelectedDirectSlides([]);
    setLocalWorkspaceSurfaceState(surface);
  };
  const { fetchSlidesByIdBatches, isHydratingManualManifestSlides } = useManifestSlideMetadata({
    labId,
    manualManifestGroupIntoSingleCase,
    manualManifestSlideIdsToLoad,
    open:
      open &&
      (hasChosenEntryIntent ||
        Boolean(manifestSurfaceState) ||
        !isEmpty(draftCases) ||
        !isEmpty(manualManifestSlideIdsToLoad)),
    updateManifestDialogUrlState,
  });

  const {
    procedures: existingProcedures,
    isLoading: isCasesLoading,
    isError: isCasesError,
    studyCasesCount,
  } = useAllStudyProcedures(studyId, open);

  const pendingSlidesEnabled = open && !isHydratingManualManifestSlides;
  const {
    allPendingSlide: allLabPendingSlides,
    allMockCasesOfPendingSlides,
    isPendingSlidesLoading: isLabPendingSlidesLoading,
    pendingSlidesError: labPendingSlidesError,
  } = useAllPendingSlides({ enabled: pendingSlidesEnabled, scope: 'lab' });
  const {
    allMockCasesOfPendingSlides: allMockCasesOfStudyPendingSlides,
    isPendingSlidesLoading: isStudyPendingSlidesLoading,
    pendingSlidesError: studyPendingSlidesError,
  } = useAllPendingSlides({
    enabled: pendingSlidesEnabled,
    scope: 'study',
    studyId,
  });
  const isPendingSlidesLoading = isLabPendingSlidesLoading || isStudyPendingSlidesLoading;
  const unassignedSlideIds = useMemo(
    () =>
      new Set(map(concat(allLabPendingSlides || [], flatMap(allMockCasesOfStudyPendingSlides || [], 'slides')), 'id')),
    [allLabPendingSlides, allMockCasesOfStudyPendingSlides]
  );
  const workflowInventoryLoading = isCasesLoading || isStudyPendingSlidesLoading;
  const caseOrganizationWorkflowMetrics = useMemo(
    () =>
      getCaseOrganizationWorkflowMetrics(
        getStudyWorkflowInventory(existingProcedures, flatMap(allMockCasesOfStudyPendingSlides || [], 'slides')),
        existingProcedures.length
      ),
    [allMockCasesOfStudyPendingSlides, existingProcedures]
  );

  const editableExistingAndMockCases = useMemo(
    () => concat(allMockCasesOfPendingSlides || [], existingProcedures || []),
    [allMockCasesOfPendingSlides, existingProcedures]
  );

  const resolveManifestDraftCases = useCallback(
    (manifestCases: Procedure[], targetsBySlideId?: Record<string, ManifestCaseTarget>) =>
      resolveDraftCases({ manifestCases, existingProcedures, overrideCases, targetsBySlideId }),
    [existingProcedures, overrideCases]
  );

  const baseManifestCases = useMemo(
    () => removeSlidesFromManifestCases(draftCases, removedSlideIds),
    [draftCases, removedSlideIds]
  );

  const baseResolvedManifestCases = useMemo(
    () => resolveManifestDraftCases(baseManifestCases),
    [baseManifestCases, resolveManifestDraftCases]
  );

  const manifestCaseTargets = useMemo(
    () =>
      joinNewTargetsToUniqueExistingCases({
        cases: applyManifestDraftPatches(
          baseManifestCases.filter((procedure) => procedure.draftTarget === undefined),
          casePatchesById,
          slidePatchesById
        ),
        existingProcedures,
        targetsBySlideId: getManifestCaseTargets({
          cases: baseManifestCases,
          existingProcedures,
          assignmentsBySlideId: existingCaseAssignmentsBySlideId,
        }),
      }),
    [
      baseManifestCases,
      casePatchesById,
      existingCaseAssignmentsBySlideId,
      existingProcedures,
      draftCases,
      removedSlideIds,
      slidePatchesById,
    ]
  );

  const existingCaseAssignmentResolution = useMemo(
    () =>
      resolveManifestDraftAssignments({
        cases: baseManifestCases,
        existingProcedures,
        assignmentsBySlideId: manifestCaseTargets,
        casePatchesById,
        slidePatchesById,
        labId,
        studyId,
      }),
    [baseManifestCases, casePatchesById, slidePatchesById, existingProcedures, manifestCaseTargets, labId, studyId]
  );
  const currentDraftCases: ManifestDraftCase[] = useMemo(() => {
    const resolvedCases = existingCaseAssignmentResolution.missingTargetCount
      ? existingCaseAssignmentResolution.cases
      : resolveManifestDraftCases(existingCaseAssignmentResolution.cases, manifestCaseTargets);
    return map(resolvedCases, (procedure) => {
      const [slide] = procedure.slides || [];
      const isNewCase =
        procedure.id < 0 && size(procedure.slides) === 1 && slide && manifestCaseTargets[slide.id] === 'new';
      if (!isNewCase || has(casePatchesById, [procedure.id, 'label'])) return procedure;
      const fileNameLabel = caseNameFromFileName(slide.originalFileName, slide.id);
      const currentLabel = procedure.label?.trim();
      const isFileNameDerivedLabel =
        !currentLabel ||
        currentLabel === 'Unnamed' ||
        currentLabel === slide.originalFileName ||
        currentLabel === fileNameLabel ||
        caseNameFromFileName(currentLabel, slide.id) === fileNameLabel;
      if (!isFileNameDerivedLabel) return procedure;
      const label = useFileNameForNewCaseLabels ? fileNameLabel : '';
      return { ...procedure, label, name: label };
    });
  }, [
    casePatchesById,
    existingCaseAssignmentResolution,
    manifestCaseTargets,
    resolveManifestDraftCases,
    useFileNameForNewCaseLabels,
  ]);
  const isCaseLabelEditable = useCallback(
    (slideId: string, caseId?: number) => {
      const procedure = find(
        currentDraftCases,
        (candidate) => candidate.id === caseId && some(candidate.slides, { id: slideId })
      );
      const target = procedure?.draftTarget === undefined ? manifestCaseTargets[slideId] : procedure.draftTarget;
      return target === 'new';
    },
    [currentDraftCases, manifestCaseTargets]
  );
  const [isSelectSlidesMenuOpen, setIsSelectSlidesMenuOpen] = useState(false);
  const isChoosingEntryIntent =
    !hasChosenEntryIntent &&
    !manifestSurfaceState &&
    isEmpty(currentDraftCases) &&
    isEmpty(manualManifestSlideIdsToLoad);

  const caseOrganizationPlan = useMemo(
    () =>
      buildCaseOrganizationPlan({
        canonicalDraft: currentDraftCases,
        existingCases: existingProcedures,
        executionMode: isExecutionModeValid ? executionMode : 'routine',
        targetLabId: labId,
        targetStudyId: studyId,
      }),
    [currentDraftCases, existingProcedures, labId, executionMode, isExecutionModeValid, studyId]
  );
  const {
    applyCaseOrganization,
    assignmentFailureMessage,
    completedBatchCount,
    createProceduresIsLoading,
    failedCaseBatchCount,
    failedSchedulingCount,
    overrideFailed,
    resetExecutionState,
    retryFailedCaseBatches,
    runningDialogOpen,
    setRunningDialogOpen,
    totalBatches,
  } = useCaseOrganizationExecution({ caseOrganizationPlan, labId, studyId });

  const { slidesFields: fields } = useEditableFields();
  const {
    activeDestinationConfirmation,
    activePendingOverwrite,
    activeWorkbookContext,
    closeFileMenuRef,
    currentPlanSignature,
    destinationError,
    destinationLookup,
    destinationSourcesLoading,
    destinationTransfer,
    disableInput,
    dismissDestinationConfirmation,
    dismissFileImport,
    exportedPlanSignature,
    foreignContext,
    handleFileChange,
    importManifestFile,
    invalidateUpload,
    isResolvingUploadedManifest,
    isReturningToOriginal,
    manifestInputRef,
    overwriteAccepted,
    reconcileWorkbookContext,
    returnToOriginalPlan,
    setOverwriteAccepted,
    switchManifestDestination,
    uploadScope,
  } = useManifestFileImport({
    open,
    labId,
    studyId,
    executionMode,
    canIncludeAssigned,
    canSwitchLabs,
    draftUploadScope,
    operationErrors,
    sourcesLoading:
      isCasesLoading ||
      isHydratingManualManifestSlides ||
      isPendingSlidesLoading ||
      Boolean(procedureFieldContext?.uiSettingsLoading),
    hasSourceError: Boolean(isCasesError || labPendingSlidesError || studyPendingSlidesError),
    existingProcedures,
    editableExistingAndMockCases,
    pendingCaseCount: size(allMockCasesOfPendingSlides),
    procedureFieldContext,
    fields,
    rowsPerPage,
    fetchSlidesByIdBatches,
    updateManifestDialogUrlState,
    onDraftAccepted: () => {
      setLocalWorkspaceSurface(null);
      setManifestSurface('Manifest');
    },
    onBeforeHandlingChange: () => setCaseOrganizationReviewOpen(false),
  });

  const onCloseDialog = (event?: any) => {
    onClose(event);
    setManifestDialogUrlState(undefined, 'replaceIn');
    setManifestSurface(undefined, 'replaceIn');
    if (caseManifestSelectionMode) {
      setCaseManifestSelectionMode(undefined, 'replaceIn');
    }
    setCaseManifestSelectedRows(undefined, 'replaceIn');
    setCaseManifestOmittedRows(undefined, 'replaceIn');
    resetExecutionState();
    if (manifestInputRef.current) {
      manifestInputRef.current.value = '';
    }
  };

  const pendingSlidesThatNotInTheManifest = useMemo(() => {
    return filter(
      allMockCasesOfPendingSlides,
      (mockCase) =>
        !find(currentDraftCases, (updatesCase) => some(updatesCase.slides, { id: first(mockCase.slides)?.id }))
    );
  }, [allMockCasesOfPendingSlides, currentDraftCases]);

  const studyScopedPendingSlidesThatNotInTheManifest = useMemo(
    () =>
      filter(
        allMockCasesOfStudyPendingSlides,
        (mockCase) =>
          !find(currentDraftCases, (updatesCase) => some(updatesCase.slides, { id: first(mockCase.slides)?.id }))
      ),
    [allMockCasesOfStudyPendingSlides, currentDraftCases]
  );

  const warningMessages = draftWarnings;
  const studyScopedPendingSlidesCount = studyScopedPendingSlidesThatNotInTheManifest.length;
  const selectionSourceEntries = useMemo(
    () =>
      [
        canAddSlidesAcrossLabs
          ? {
              id: crossLabSearchSurface as WorkspaceSurface,
              label: 'Find slides from other labs',
            }
          : null,
        canReadStudySource && (canIncludeAssigned || isPendingSlidesLoading || studyScopedPendingSlidesCount > 0)
          ? {
              id: 'Study Lost and Found Slides' as WorkspaceSurface,
              label: 'Slides awaiting assignment',
            }
          : null,
        canReadLabSource &&
        (canIncludeAssigned ||
          (!isPendingSlidesLoading &&
            pendingSlidesThatNotInTheManifest.length > 0 &&
            studyScopedPendingSlidesCount !== pendingSlidesThatNotInTheManifest.length))
          ? {
              id: 'Lab Lost and Found Slides' as WorkspaceSurface,
              label: 'Lab lost and found slides',
            }
          : null,
      ].filter(Boolean) as { id: WorkspaceSurface; label: string }[],
    [
      canAddSlidesAcrossLabs,
      canReadStudySource,
      canReadLabSource,
      canIncludeAssigned,
      isPendingSlidesLoading,
      pendingSlidesThatNotInTheManifest.length,
      studyScopedPendingSlidesCount,
    ]
  );
  const visibleSurfaces = [...map(selectionSourceEntries, 'id'), 'Manifest' as ManifestSurface];
  const defaultAddSlidesSurface: ManifestSurface = !isEmpty(studyScopedPendingSlidesThatNotInTheManifest)
    ? 'Study Lost and Found Slides'
    : !isEmpty(pendingSlidesThatNotInTheManifest)
    ? 'Lab Lost and Found Slides'
    : 'Manifest';
  const effectiveManifestSurface =
    manifestSurfaceState === 'Manifest' || manifestSurfaceState === 'Not Ingested Slides'
      ? 'Manifest'
      : includes(visibleSurfaces, manifestSurfaceState as ManifestSurface)
      ? (manifestSurfaceState as ManifestSurface)
      : manifestSurfaceState
      ? defaultAddSlidesSurface
      : 'Manifest';
  const activeWorkspaceSurface = localWorkspaceSurface || effectiveManifestSurface;
  const isOrganizationView = activeWorkspaceSurface === 'Manifest';
  const isSelectingSlides =
    activeWorkspaceSurface === crossLabSearchSurface ||
    activeWorkspaceSurface === 'Lab Lost and Found Slides' ||
    activeWorkspaceSurface === 'Study Lost and Found Slides';
  const selectSlidesButtonLabel = 'Assign more slides';
  const sourceScope = activeWorkspaceSurface === 'Study Lost and Found Slides' ? 'study' : 'lab';
  const canReadActiveSource = sourceScope === 'study' ? canReadStudySource : canReadLabSource;
  const activePendingSlidesError = sourceScope === 'study' ? studyPendingSlidesError : labPendingSlidesError;
  const pendingSlidesCasesToDisplay =
    activeWorkspaceSurface === 'Study Lost and Found Slides'
      ? studyScopedPendingSlidesThatNotInTheManifest
      : pendingSlidesThatNotInTheManifest;
  const {
    addDirectSlidesToManifest,
    addSelectedPendingSlidesToManifest,
    selectedPendingSlidesCount,
    sourcePanelProps,
  } = useManifestSourceSelection({
    open,
    labId,
    studyId,
    activeWorkspaceSurface,
    currentDraftCases,
    pendingSlidesCasesToDisplay,
    sourceScope,
    canReadActiveSource,
    canReadStudySource,
    canIncludeAssigned,
    isPendingSlidesLoading,
    activePendingSlidesError,
    pendingSlidesSelectionAction,
    disabled: disableInput,
    updateManifestDialogUrlState,
  });

  const changeWorkspaceSurface = (newSurface: WorkspaceSurface) => {
    setIsSelectSlidesMenuOpen(false);
    setHasChosenEntryIntent(true);
    if (newSurface === crossLabSearchSurface) {
      setLocalWorkspaceSurface(newSurface);
      return;
    }
    setLocalWorkspaceSurface(null);
    setManifestSurface(newSurface);
  };

  const openOrganization = () => {
    setLocalWorkspaceSurface(null);
    setManifestSurface('Manifest');
  };

  const handleSlideAddition = (choice: SlideAdditionChoice, { continueAdding }: SlideAdditionOptions) => {
    setSlideAdditionDialogOpen(false);
    if (activeWorkspaceSurface === crossLabSearchSurface) {
      addDirectSlidesToManifest(selectedDirectSlides, choice);
      setSelectedDirectSlides([]);
      setDirectSearchResetKey((currentKey) => currentKey + 1);
      if (!continueAdding) openOrganization();
      return;
    }
    if (addSelectedPendingSlidesToManifest(choice) && !continueAdding) openOrganization();
  };

  const isManifestDataLoading =
    isCasesLoading ||
    isResolvingUploadedManifest ||
    isHydratingManualManifestSlides ||
    isProcedureFieldOptionsLoading(procedureFieldContext);
  const caseAssignmentStatusLoading = workflowInventoryLoading || isManifestDataLoading;
  const shouldValidateManifestDraft = !isManifestDataLoading;
  const additionalIssueSlideIds = useMemo(
    () => [
      ...collectExplicitNewCaseCollisionSlideIds(currentDraftCases, existingProcedures, manifestCaseTargets),
      ...flatMap(currentDraftCases, (procedure) =>
        (procedure.slides || []).filter((slide) => manifestCaseTargets[slide.id] === null).map((slide) => slide.id)
      ),
    ],
    [currentDraftCases, existingProcedures, manifestCaseTargets]
  );
  const {
    applyCellValueChangedClass,
    editableManifestCaseIds,
    editableManifestSlideIds,
    onBulkEditSave,
    onCaseDataChange,
    onPageChange,
    onRowsPerPageChange,
    onShowOnlyErrorsSwitchChange,
    onSlideDataChange,
    pageCases,
    removeSelectedManifestSlidesFromManifest,
    selectedManifestSlidesCount,
    totalCases,
    totalSlides,
    unresolvedRowCount,
  } = useManifestPlanEditing({
    currentDraftCases,
    baseManifestCases,
    baseResolvedManifestCases,
    fields,
    procedureFieldContext,
    shouldValidateManifestDraft,
    showOnlyErrors,
    showOnlyUnassignedSlides,
    unassignedSlideIds: Array.from(unassignedSlideIds),
    additionalIssueSlideIds,
    page,
    rowsPerPage,
    isCaseLabelEditable,
    updateManifestDialogUrlState,
    setCaseManifestSelectedRows,
    setCaseManifestOmittedRows,
  });

  const validationErrorMessages = useMemo(
    () =>
      shouldValidateManifestDraft
        ? [
            ...collectDuplicateSlideRowErrors(
              baseManifestCases,
              existingCaseAssignmentResolution.cases,
              currentDraftCases
            ),
            ...collectManifestCaseLabelErrors(currentDraftCases, existingProcedures),
            ...collectExplicitNewCaseLabelErrors(currentDraftCases, existingProcedures, manifestCaseTargets),
            ...collectCaseSharedMetadataErrors(existingCaseAssignmentResolution.cases),
            ...collectManifestValidationErrors({
              cases: currentDraftCases,
              fields,
              procedureFieldContext,
              rowsPerPage,
            }),
          ]
        : [],
    [
      baseManifestCases,
      currentDraftCases,
      existingCaseAssignmentResolution,
      existingProcedures,
      fields,
      manifestCaseTargets,
      procedureFieldContext,
      rowsPerPage,
      shouldValidateManifestDraft,
    ]
  );

  const errorMessages = useMemo(
    () => Array.from(new Set([...operationErrors, ...manualManifestErrorMessages, ...validationErrorMessages])),
    [manualManifestErrorMessages, operationErrors, validationErrorMessages]
  );
  const planErrorMessages = useMemo(
    () => errorMessages.filter((message) => !operationErrors.includes(message)),
    [errorMessages, operationErrors]
  );

  const [duplicateRowsSource, setDuplicateRowsSource] = useState<'file' | 'plan' | null>(null);
  const draftDuplicateGroups = useMemo(
    () =>
      buildDraftDuplicateGroups({
        cases: baseManifestCases,
        casePatchesById,
        slidePatchesById,
        procedureFieldContext,
        targetsBySlideId: manifestCaseTargets,
      }),
    [baseManifestCases, casePatchesById, slidePatchesById, procedureFieldContext, manifestCaseTargets]
  );
  const activeDuplicateGroups = draftDuplicateGroups;
  // Bulk naming rewrites rows by slide, so it would silently collapse duplicates the user has not chosen between.
  const hasDuplicateRows = !isEmpty(draftDuplicateGroups);
  const applyDuplicateRowChoices = (keptOptionIdByGroupId: Record<string, string>) => {
    updateManifestDialogUrlState((previousState) => keepChosenDraftRows(previousState, keptOptionIdByGroupId));
    setDuplicateRowsSource(null);
  };

  const workbookProjection = useMemo(
    () =>
      isChoosingEntryIntent
        ? null
        : projectManifestWorkbookPlan({
            cases: currentDraftCases,
            sourceCases: concat(editableExistingAndMockCases, draftCases),
            existingCases: existingProcedures,
            targetsBySlideId: getManifestCaseTargets({
              cases: currentDraftCases,
              existingProcedures,
              assignmentsBySlideId: manifestCaseTargets,
            }),
            procedureFieldContext,
          }),
    [
      currentDraftCases,
      editableExistingAndMockCases,
      existingProcedures,
      isChoosingEntryIntent,
      draftCases,
      manifestCaseTargets,
      procedureFieldContext,
    ]
  );
  // A pending review fills the draft after loading ends, so a queued download waits for it. A case-list error keeps the
  // review intent for a later refetch, so it must not hold the download.
  const isReviewFillPending = Boolean(
    (reviewExistingCasesWhenAvailable && !isCasesError) || reviewUnassignedSlidesWhenAvailable
  );
  const { downloadCurrentPlan, dismissWorkbookExportError, isExportingWorkbook, workbookExportError } =
    useManifestWorkbookExport({
      loading: destinationSourcesLoading || isReviewFillPending,
      authorized: canIncludeAssigned,
      open,
      canSwitchLabs,
      currentPlanSignature,
      executionMode,
      existingProcedures,
      exportedPlanSignature,
      labId,
      procedureFieldContext,
      projection: workbookProjection,
      studyId,
    });

  const hasUploadedManifest = !isEmpty(draftCases) || !isEmpty(operationErrors);
  const hasResettableManifestChanges = !isEmpty(casePatchesById) || !isEmpty(slidePatchesById);

  const resetManifestChanges = useCallback(async () => {
    if (!hasResettableManifestChanges) {
      return;
    }

    const confirmed = await confirmWithModal({
      title: 'Reset Slide Organization Changes',
      text: (
        <>
          <span>
            This will discard unsaved edits made while reviewing slide organization and restore case and slide fields to
            their current values.
          </span>
          <br />
          <br />
          <span>
            This does not remove uploaded slides manifest rows, slides already added to the organization, or other
            composition changes.
          </span>
        </>
      ),
      confirmButtonProps: { title: 'Reset Changes' },
      cancelButtonProps: { title: 'Cancel' },
    });

    if (confirmed) {
      updateManifestDialogUrlState({ casePatchesById: {}, slidePatchesById: {} });
    }
  }, [confirmWithModal, hasResettableManifestChanges, updateManifestDialogUrlState]);

  const onExecutionModeChange = (event: ChangeEvent<HTMLInputElement>) => {
    invalidateUpload();
    setCaseOrganizationReviewOpen(false);
    updateManifestDialogUrlState({
      executionMode: event.target.value as CaseOrganizationExecutionMode,
    });
  };
  const runDisabledReason = useMemo(() => {
    if (!isExecutionModeValid) {
      return 'Choose a supported Existing case handling option before reviewing changes.';
    }

    if (!canIncludeAssigned) {
      return 'You do not have permission to apply case assignments.';
    }

    if (isCasesError) {
      return 'Could not load all study cases. Please contact support.';
    }

    if (createProceduresIsLoading) {
      return 'Wait for this case assignment to finish.';
    }

    if (!isEmpty(operationErrors)) {
      return 'Resolve the import errors or keep the current case assignments before reviewing changes.';
    }

    if (showOnlyErrors && !isEmpty(planErrorMessages)) {
      return 'Turn off Show only issues before submitting.';
    }

    if (isHydratingManualManifestSlides) {
      return 'Wait for slide metadata to finish loading.';
    }

    if (isManifestDataLoading) {
      return 'Wait for study data to finish loading.';
    }

    return null;
  }, [
    isExecutionModeValid,
    canIncludeAssigned,
    isCasesError,
    createProceduresIsLoading,
    operationErrors,
    showOnlyErrors,
    planErrorMessages,
    isHydratingManualManifestSlides,
    isManifestDataLoading,
  ]);

  const assignmentIssues = useMemo(() => {
    const issues = [...planErrorMessages];
    if (!isCasesLoading && existingCaseAssignmentResolution.missingTargetCount > 0) {
      const count = existingCaseAssignmentResolution.missingTargetCount;
      issues.push(count === 1 ? '1 slide still needs a target case.' : `${count} slides still need a target case.`);
    }
    return issues;
  }, [existingCaseAssignmentResolution.missingTargetCount, isCasesLoading, planErrorMessages]);
  const newCaseCollisionSlideIds = useMemo(
    () => collectExplicitNewCaseCollisionSlideIds(currentDraftCases, existingProcedures, manifestCaseTargets),
    [currentDraftCases, existingProcedures, manifestCaseTargets]
  );
  const canNameCollidingCasesFromFileNames = useMemo(() => {
    if (isCasesLoading || isCasesError || isEmpty(newCaseCollisionSlideIds) || isEmpty(assignmentIssues)) return false;
    const labelIssues = [
      ...collectManifestCaseLabelErrors(currentDraftCases, existingProcedures),
      ...collectExplicitNewCaseLabelErrors(currentDraftCases, existingProcedures, manifestCaseTargets),
    ];
    if (assignmentIssues.some((issue) => !labelIssues.includes(issue))) return false;
    const collisionSlideIds = new Set(newCaseCollisionSlideIds);
    const namedCases = flatMap(currentDraftCases, (procedure) => {
      const retainedSlides = filter(procedure.slides, (slide) => !collisionSlideIds.has(slide.id));
      const namedSlides = filter(procedure.slides, (slide) => collisionSlideIds.has(slide.id));
      return [
        ...(isEmpty(retainedSlides) ? [] : [{ ...procedure, slides: retainedSlides }]),
        ...map(namedSlides, (slide) => ({
          ...procedure,
          label: caseNameFromFileName(slide.originalFileName, slide.id),
          slides: [slide],
        })),
      ];
    });
    return (
      isEmpty(collectManifestCaseLabelErrors(namedCases, existingProcedures)) &&
      isEmpty(collectExplicitNewCaseLabelErrors(namedCases, existingProcedures, manifestCaseTargets)) &&
      isEmpty(collectCaseSharedMetadataErrors(namedCases)) &&
      isEmpty(collectManifestValidationErrors({ cases: namedCases, fields, procedureFieldContext, rowsPerPage }))
    );
  }, [
    assignmentIssues,
    currentDraftCases,
    existingProcedures,
    fields,
    isCasesError,
    isCasesLoading,
    manifestCaseTargets,
    newCaseCollisionSlideIds,
    procedureFieldContext,
    rowsPerPage,
  ]);
  const nameCollidingCasesFromFileNames = useCallback(() => {
    setCaseOrganizationReviewOpen(false);
    updateManifestDialogUrlState((previousState) => ({
      ...newCaseCollisionSlideIds.reduce(
        (state, slideId) =>
          updateManifestCaseTarget({
            previousState: state,
            currentDraftCases,
            slideId,
            previousTarget: has(manifestCaseTargets, [slideId]) ? manifestCaseTargets[slideId] : null,
            target: 'new',
            useFileNameForCaseLabel: true,
          }),
        previousState
      ),
      useFileNameForNewCaseLabels: true,
    }));
  }, [currentDraftCases, manifestCaseTargets, newCaseCollisionSlideIds, updateManifestDialogUrlState]);
  const slidesNeedingFileNameCases = useMemo(
    () =>
      flatMap(currentDraftCases, (procedure) =>
        filter(procedure.slides, (slide) => {
          const target = has(manifestCaseTargets, [slide.id]) ? manifestCaseTargets[slide.id] : null;
          const savedTarget =
            typeof target === 'number' && some(existingProcedures, (existingCase) => existingCase.id === target);
          if (savedTarget || (target === null && some(existingProcedures, { id: procedure.id }))) return false;
          const expectedLabel = caseNameFromFileName(slide.originalFileName, slide.id);
          const currentLabel = casePatchesById[procedure.id]?.label ?? procedure.label ?? '';
          return currentLabel !== expectedLabel || size(procedure.slides) !== 1;
        })
      ),
    [casePatchesById, currentDraftCases, existingProcedures, manifestCaseTargets]
  );
  const createCasesFromFileNames = useCallback(() => {
    setCaseOrganizationReviewOpen(false);
    updateManifestDialogUrlState((previousState) => ({
      ...slidesNeedingFileNameCases.reduce(
        (state, slide) =>
          updateManifestCaseTarget({
            previousState: state,
            currentDraftCases,
            slideId: slide.id,
            previousTarget: has(manifestCaseTargets, [slide.id]) ? manifestCaseTargets[slide.id] : null,
            target: 'new',
            useFileNameForCaseLabel: true,
          }),
        previousState
      ),
      useFileNameForNewCaseLabels: true,
    }));
  }, [currentDraftCases, manifestCaseTargets, slidesNeedingFileNameCases, updateManifestDialogUrlState]);
  const hasAssignmentChanges = !isEmpty(currentDraftCases) && !caseOrganizationPlan.isNoOp;
  const footerGuidance =
    slidesNeedingFileNameCases.length > 0
      ? 'Indication and biopsy site are optional.'
      : hasAssignmentChanges
      ? null
      : isEmpty(currentDraftCases)
      ? 'Add slides, or upload a spreadsheet.'
      : canSwitchLabs
      ? 'Change a target case, or add slides from this study or another lab.'
      : 'Change a target case, or add slides.';
  const showSubmit = hasAssignmentChanges && canIncludeAssigned;
  const submissionLabel = isExecutionModeValid
    ? caseOrganizationSubmissionLabels[executionMode]
    : 'Choose an operation';
  const isRunDisabled = Boolean(runDisabledReason) || !isEmpty(assignmentIssues) || !isEmpty(operationErrors);
  const existingCaseOptions = map(
    filter(
      existingProcedures,
      (procedure) =>
        (!procedure.labId || procedure.labId === labId) && (!procedure.studyId || procedure.studyId === studyId)
    ),
    (procedure) => ({ id: procedure.id, label: procedure.label || 'Unnamed case' })
  );
  const targetCaseOptions = [{ id: 'new' as const, label: 'New case' }, ...existingCaseOptions];
  const savedCasesBySlideId = useMemo(() => {
    const assignments = new Map<string, Procedure[]>();
    for (const procedure of existingProcedures) {
      for (const slide of procedure.slides || []) {
        const cases = assignments.get(slide.id) || [];
        assignments.set(slide.id, [...cases, procedure]);
      }
    }
    return assignments;
  }, [existingProcedures]);
  const draftLabelsByCaseId = useMemo(
    () => new Map(currentDraftCases.map((procedure) => [procedure.id, procedure.label])),
    [currentDraftCases]
  );
  const getCurrentCaseDescription = (slideId: string) => {
    const savedCases = savedCasesBySlideId.get(slideId);
    if (savedCases?.length) return savedCases.map((procedure) => procedure.label || 'Unnamed case').join('; ');
    if (isCasesLoading || isLabPendingSlidesLoading) return 'Checking current assignment';
    if (unassignedSlideIds.has(slideId)) return 'Not assigned to a case';
    return 'Current case unavailable';
  };
  const getRowTarget = (rowId: string) => {
    const { baseRowId, innerRowId } = unwoundRowMetadataFromId(rowId);
    const slideId = String(innerRowId);
    if (draftDuplicateGroups.some((group) => group.id === slideId)) {
      const procedure = currentDraftCases.find((candidate) => candidate.id === Number(baseRowId));
      if (procedure?.draftTarget !== undefined) return procedure.draftTarget;
    }
    return has(manifestCaseTargets, [slideId]) ? manifestCaseTargets[slideId] : Number(baseRowId);
  };
  const existingCaseAssignmentColumns: GridColDef[] = [
    {
      field: 'slideId',
      headerName: 'Slide ID',
      width: 240,
      editable: false,
      renderCell: ({ id }) => {
        const { baseRowId, innerRowId } = unwoundRowMetadataFromId(String(id));
        const procedure = currentDraftCases.find((candidate) => candidate.id === Number(baseRowId));
        return procedure?.draftIdentifier ? procedure.draftIdentifier.slideId || '' : String(innerRowId);
      },
    },
    {
      field: 'currentCaseAssignment',
      headerName: 'Current case',
      width: 230,
      sortable: false,
      editable: false,
      renderCell: ({ id }) => {
        const { innerRowId } = unwoundRowMetadataFromId(String(id));
        return getCurrentCaseDescription(String(innerRowId));
      },
    },
    {
      field: 'targetCaseAssignment',
      headerName: 'Assign to case',
      width: 260,
      sortable: false,
      cellClassName: ({ id }) => (getRowTarget(String(id)) === null ? 'cell-error' : ''),
      renderCell: ({ id, row }) => {
        const { baseRowId, innerRowId } = unwoundRowMetadataFromId(String(id));
        const slideId = String(innerRowId);
        const target = getRowTarget(String(id));
        return (
          <CaseManifestTargetCaseCell
            options={targetCaseOptions}
            target={target}
            slideLabel={row.originalFileName || slideId}
            disabled={disableInput || isCasesError || !canIncludeAssigned}
            loading={isCasesLoading}
            onChange={(nextTarget) => {
              setCaseOrganizationReviewOpen(false);
              updateManifestDialogUrlState((previousState) =>
                updateManifestCaseTarget({
                  previousState,
                  currentDraftCases,
                  draftCaseId: Number(baseRowId),
                  slideId,
                  previousTarget: target,
                  target: nextTarget,
                })
              );
            }}
          />
        );
      },
    },
    {
      field: 'label',
      headerName: 'New case name',
      width: 210,
      editable: false,
      renderCell: ({ id, row }) => {
        const { baseRowId, innerRowId } = unwoundRowMetadataFromId(String(id));
        if (getRowTarget(String(id)) !== 'new') return 'Not applicable';
        const slideId = String(innerRowId);
        return (
          <TextField
            size="small"
            fullWidth
            required
            placeholder="New case name"
            value={draftLabelsByCaseId.get(Number(baseRowId)) || ''}
            disabled={disableInput || isCasesError || !canIncludeAssigned}
            inputProps={{ 'aria-label': `New case name for ${row.originalFileName || slideId}` }}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            onChange={(event) => {
              setCaseOrganizationReviewOpen(false);
              onCaseDataChange(Number(baseRowId), { label: event.target.value }, slideId);
            }}
          />
        );
      },
    },
    {
      field: 'assignmentChange',
      headerName: 'Change',
      width: 300,
      sortable: false,
      editable: false,
      renderCell: ({ id }) => {
        const { baseRowId, innerRowId } = unwoundRowMetadataFromId(String(id));
        const draftCase = currentDraftCases.find((procedure) => procedure.id === Number(baseRowId));
        const rowIssues = draftCase?.draftIssues;
        if (rowIssues?.length) {
          return (
            <Typography color="error" variant="body2">
              {rowIssues.map((issue) => issue.message).join(' ')}
            </Typography>
          );
        }
        if (draftDuplicateGroups.some((group) => group.id === String(innerRowId))) {
          return (
            <Typography color="error" variant="body2">
              Duplicate slide row. Keep one row.
            </Typography>
          );
        }
        const target = getRowTarget(String(id));
        if (target === null) return 'Select a destination';
        const savedCases = savedCasesBySlideId.get(String(innerRowId)) || [];
        if (savedCases.length === 1 && savedCases[0].id === target) return 'No assignment change';
        const destination =
          target === 'new'
            ? `New case: ${draftLabelsByCaseId.get(Number(baseRowId)) || 'Name required'}`
            : find(existingCaseOptions, { id: target })?.label || 'Unavailable case';
        return savedCases.length
          ? `${getCurrentCaseDescription(String(innerRowId))} -> ${destination}`
          : `Assign to ${destination}`;
      },
    },
  ];
  const dialogLoadingStatusText = isResolvingUploadedManifest
    ? 'Validating the case assignment…'
    : isHydratingManualManifestSlides
    ? 'Loading slide information for case assignment…'
    : isCasesLoading
    ? CASE_ORGANIZATION_WORKFLOW_LOADING_MESSAGE
    : isProcedureFieldOptionsLoading(procedureFieldContext)
    ? 'Loading case assignment options…'
    : showOnlyUnassignedSlides && isPendingSlidesLoading
    ? 'Loading slides awaiting assignment…'
    : null;
  const caseListLoadingIcon = isCasesLoading ? <CircularProgress color="inherit" size={16} /> : undefined;

  const selectSlidesControl = (
    <CaseManifestSlideSourceMenu
      anchorEl={selectSlidesAnchorEl}
      disabled={isResolvingUploadedManifest}
      entries={selectionSourceEntries}
      isChoosingEntryIntent={isChoosingEntryIntent}
      label={selectSlidesButtonLabel}
      onChange={changeWorkspaceSurface}
      onOpenChange={setIsSelectSlidesMenuOpen}
      open={isSelectSlidesMenuOpen}
      selectedSurface={activeWorkspaceSurface === 'Manifest' ? defaultAddSlidesSurface : activeWorkspaceSurface}
      setAnchorEl={setSelectSlidesAnchorEl}
    />
  );
  const selectedSlideCount =
    activeWorkspaceSurface === crossLabSearchSurface ? selectedDirectSlides.length : selectedPendingSlidesCount;
  const selectionActionDisabled =
    !selectedSlideCount ||
    !canIncludeAssigned ||
    isResolvingUploadedManifest ||
    isHydratingManualManifestSlides ||
    (activeWorkspaceSurface !== crossLabSearchSurface &&
      (sourcePanelProps.isLoading || sourcePanelProps.sourceUnavailable));
  const unassignedStudySlideCount = flatMap(allMockCasesOfStudyPendingSlides, 'slides').length;
  const canReviewUnassigned =
    canReadStudySource &&
    canIncludeAssigned &&
    !isStudyPendingSlidesLoading &&
    !studyPendingSlidesError &&
    unassignedStudySlideCount > 0;
  const appendPendingStudyCases = (previousState: ManifestDialogUrlState): ManifestDialogUrlState => {
    const currentCases = previousState.draftCases || [];
    const includedSlideIds = new Set(map(flatMap(currentCases, 'slides'), 'id'));
    const pendingCasesToAdd = filter(
      allMockCasesOfStudyPendingSlides || [],
      (procedure) => !some(procedure.slides, (slide) => includedSlideIds.has(slide.id))
    );
    const newPendingCases = assignManifestDraftCaseIds({
      cases: pendingCasesToAdd,
      retainedCases: currentCases,
      casePatchesById: previousState.casePatchesById,
    });
    return {
      ...previousState,
      draftCases: concat(currentCases, newPendingCases),
    };
  };
  const appendExistingStudyCases = (previousState: ManifestDialogUrlState): ManifestDialogUrlState => ({
    ...previousState,
    draftCases: addSavedCasesToDraft(previousState.draftCases || [], existingProcedures),
  });
  const reviewUnassignedSlides = (additionalState: Partial<ManifestDialogUrlState> = {}) => {
    updateManifestDialogUrlState((previousState) => {
      let nextState: ManifestDialogUrlState = {
        ...previousState,
        ...additionalState,
        showOnlyUnassignedSlides: true,
        page: 1,
      };
      if (isCasesLoading) {
        nextState.reviewExistingCasesWhenAvailable = true;
      } else if (!isCasesError) {
        nextState = appendExistingStudyCases(nextState);
      }
      if (!isStudyPendingSlidesLoading && !studyPendingSlidesError) {
        nextState = appendPendingStudyCases(nextState);
      }
      return nextState;
    });
    setHasChosenEntryIntent(true);
    openOrganization();
  };
  const onShowOnlyUnassignedSlidesChange = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = Boolean(event.target.checked);
    updateManifestDialogUrlState((previousState) => {
      let nextState: ManifestDialogUrlState = { ...previousState, showOnlyUnassignedSlides: checked, page: 1 };
      if (checked) {
        if (isStudyPendingSlidesLoading) {
          nextState.includeStudyPendingSlidesWhenAvailable = true;
        } else if (!studyPendingSlidesError) {
          nextState = appendPendingStudyCases(nextState);
        }
      } else if (isCasesLoading) {
        nextState.reviewExistingCasesWhenAvailable = true;
      } else if (!isCasesError) {
        nextState = appendExistingStudyCases(nextState);
      }
      return nextState;
    });
  };
  const onUseFileNameForNewCaseLabelsChange = (event: ChangeEvent<HTMLInputElement>) => {
    updateManifestDialogUrlState({ useFileNameForNewCaseLabels: Boolean(event.target.checked), page: 1 });
  };
  // The study statistics already reported the case count, so an empty study never offers the entry while its list loads.
  // The count is only a hint: a loaded list that has cases still offers it, and an error keeps it available.
  const mayHaveExistingCases = !isEmpty(existingProcedures) || (isCasesLoading && (studyCasesCount ?? 0) > 0);
  const canReviewExisting = canIncludeAssigned && (isCasesError || mayHaveExistingCases);
  // A chosen workbook replaces the draft, so it supersedes a pending review; the review fill must not alter the draft
  // scope the deferred import was started against.
  const chooseManifestFile = (event: ChangeEvent<HTMLInputElement>) => {
    if (
      (reviewExistingCasesWhenAvailable ||
        reviewUnassignedSlidesWhenAvailable ||
        includeStudyPendingSlidesWhenAvailable) &&
      first(event.target.files)
    ) {
      updateManifestDialogUrlState({
        reviewExistingCasesWhenAvailable: undefined,
        reviewUnassignedSlidesWhenAvailable: undefined,
        includeStudyPendingSlidesWhenAvailable: undefined,
      });
    }
    return handleFileChange(event);
  };
  // The table view owns upload feedback, so choosing a file opens it before the import starts.
  const openOrganizationForUpload = (event: ChangeEvent<HTMLInputElement>) => {
    setHasChosenEntryIntent(true);
    openOrganization();
    return chooseManifestFile(event);
  };
  const reviewExistingCases = () => {
    updateManifestDialogUrlState((previousState) => {
      let nextState: ManifestDialogUrlState = { ...previousState, showOnlyUnassignedSlides: false, page: 1 };
      if (isCasesLoading || isCasesError) {
        nextState.reviewExistingCasesWhenAvailable = true;
      } else {
        nextState = appendExistingStudyCases(nextState);
      }
      if (isStudyPendingSlidesLoading) {
        nextState.includeStudyPendingSlidesWhenAvailable = true;
      } else if (!studyPendingSlidesError) {
        nextState = appendPendingStudyCases(nextState);
      }
      return nextState;
    });
    setHasChosenEntryIntent(true);
    openOrganization();
  };
  useEffect(() => {
    // After an error the review intent is kept so a later successful refetch still fills the table.
    if (!reviewExistingCasesWhenAvailable || !open || isCasesLoading || isCasesError) return;
    if (isEmpty(existingProcedures)) {
      updateManifestDialogUrlState({ reviewExistingCasesWhenAvailable: undefined });
      if (
        isEmpty(currentDraftCases) &&
        !reviewUnassignedSlidesWhenAvailable &&
        !includeStudyPendingSlidesWhenAvailable
      ) {
        setHasChosenEntryIntent(false);
        setManifestSurface(undefined, 'replaceIn');
      }
      return;
    }
    updateManifestDialogUrlState((previousState) => ({
      ...appendExistingStudyCases(previousState),
      reviewExistingCasesWhenAvailable: undefined,
    }));
    // Not exhaustive: the flag is cleared when consumed, so handler and draft changes must not re-trigger it.
  }, [
    reviewExistingCasesWhenAvailable,
    open,
    isCasesLoading,
    isCasesError,
    existingProcedures,
    reviewUnassignedSlidesWhenAvailable,
    includeStudyPendingSlidesWhenAvailable,
  ]);
  useEffect(() => {
    if (!reviewUnassignedSlidesWhenAvailable || !open || isStudyPendingSlidesLoading) return;
    if (canReviewUnassigned) {
      reviewUnassignedSlides({ reviewUnassignedSlidesWhenAvailable: undefined });
    } else {
      updateManifestDialogUrlState({ reviewUnassignedSlidesWhenAvailable: undefined });
    }
    // Not exhaustive: the handlers are recreated every render and must not re-trigger it; the flag is cleared when consumed.
  }, [reviewUnassignedSlidesWhenAvailable, open, isStudyPendingSlidesLoading, canReviewUnassigned]);
  useEffect(() => {
    if (!includeStudyPendingSlidesWhenAvailable || !open || isStudyPendingSlidesLoading) return;
    updateManifestDialogUrlState((previousState) => ({
      ...(!studyPendingSlidesError ? appendPendingStudyCases(previousState) : previousState),
      includeStudyPendingSlidesWhenAvailable: undefined,
    }));
    // Not exhaustive: clear the deferred flag once the pending-slide query settles.
  }, [includeStudyPendingSlidesWhenAvailable, open, isStudyPendingSlidesLoading, studyPendingSlidesError]);
  const entryContext = [currentLab?.name, find(procedureFieldContext.studies, { id: studyId })?.name]
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      <Dialog
        open={open}
        fullScreen={!isChoosingEntryIntent}
        fullWidth={isChoosingEntryIntent}
        maxWidth={false}
        PaperProps={isChoosingEntryIntent ? { sx: { maxWidth: 1000 } } : undefined}
        onClose={isChoosingEntryIntent ? onCloseDialog : undefined}
        aria-labelledby="case-organization-title"
        scroll="paper"
      >
        <DialogChromeHeader
          title={dialogTitle}
          titleId="case-organization-title"
          subtitle={isChoosingEntryIntent ? entryContext : undefined}
          backButtonAriaLabel="Back to case assignment"
          onBack={isSelectingSlides ? openOrganization : undefined}
          onClose={onCloseDialog}
          sx={{ '& h1': { fontSize: 24 } }}
        />
        {!isChoosingEntryIntent && (
          <ManifestDraftStatus
            isLoading={caseAssignmentStatusLoading}
            loadingMessage={dialogLoadingStatusText || CASE_ORGANIZATION_WORKFLOW_LOADING_MESSAGE}
            metrics={caseOrganizationWorkflowMetrics}
            onReviewUnresolvedRows={() => {
              openOrganization();
              updateManifestDialogUrlState({ showOnlyErrors: true, page: 1 });
            }}
            unresolvedRowCount={unresolvedRowCount}
          />
        )}
        {destinationTransfer &&
          destinationTransfer.destination.labId === labId &&
          destinationTransfer.destination.studyId === studyId && (
            <Alert
              severity="info"
              action={
                <Button
                  color="inherit"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => void returnToOriginalPlan()}
                  disabled={isReturningToOriginal || isResolvingUploadedManifest || createProceduresIsLoading}
                >
                  Return to the original case assignment
                </Button>
              }
            >
              {canIncludeAssigned
                ? `Case assignment destination: ${destinationTransfer.destination.studyName} (${destinationTransfer.destination.labName}).`
                : 'The original case assignment is preserved.'}
            </Alert>
          )}
        {destinationError?.scope === uploadScope && <Alert severity="error">{destinationError.message}</Alert>}
        {!isChoosingEntryIntent && (
          <CaseManifestHandling
            disabled={!canIncludeAssigned || createProceduresIsLoading || isResolvingUploadedManifest}
            executionMode={executionMode}
            isExecutionModeValid={isExecutionModeValid}
            onChange={onExecutionModeChange}
          />
        )}
        <DialogContent
          dividers
          sx={!isChoosingEntryIntent ? { display: 'flex', flex: 1, flexDirection: 'column', minHeight: 0 } : undefined}
        >
          {isChoosingEntryIntent ? (
            <CaseManifestEntryChoices
              canReviewUnassigned={canReviewUnassigned}
              caseListLoadingIcon={caseListLoadingIcon}
              canReviewExisting={canReviewExisting}
              isCasesError={isCasesError}
              isCasesLoading={isCasesLoading}
              ingestNotice={
                <CaseManifestIngestNotice
                  studyId={studyId}
                  hasUnassignedSlides={
                    canReadStudySource && !isStudyPendingSlidesLoading && !studyPendingSlidesError
                      ? unassignedStudySlideCount > 0
                      : undefined
                  }
                />
              }
              manifestFileControl={
                canIncludeAssigned && (
                  <>
                    <CaseManifestFileMenu
                      canDownload={false}
                      canUpload={!isResolvingUploadedManifest}
                      caseCount={0}
                      closeMenuRef={closeFileMenuRef}
                      executionMode={null}
                      exporting={false}
                      hasCurrentPlan={false}
                      onDownloadPlan={() => undefined}
                      onUploadClick={() => manifestInputRef.current?.click()}
                      resolving={isResolvingUploadedManifest}
                      slideCount={0}
                    />
                    <input
                      ref={manifestInputRef}
                      hidden
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={openOrganizationForUpload}
                      disabled={isResolvingUploadedManifest}
                      id="case-manifest-upload-input"
                    />
                  </>
                )
              }
              onReviewExisting={reviewExistingCases}
              onReviewUnassigned={() => reviewUnassignedSlides()}
              selectSlidesControl={selectSlidesControl}
              studySourceStatus={
                canReadStudySource
                  ? isStudyPendingSlidesLoading
                    ? 'loading'
                    : studyPendingSlidesError
                    ? 'error'
                    : null
                  : null
              }
              unassignedStudySlideCount={unassignedStudySlideCount}
            />
          ) : (
            <>
              <CaseManifestPlanToolbar
                title={
                  activeWorkspaceSurface === 'Study Lost and Found Slides'
                    ? 'Slides awaiting assignment'
                    : activeWorkspaceSurface === 'Lab Lost and Found Slides'
                    ? 'Lab slides without a case'
                    : activeWorkspaceSurface === crossLabSearchSurface
                    ? 'Slides from other labs'
                    : 'Case assignment'
                }
                fileMenuProps={{
                  canDownload: canIncludeAssigned,
                  canUpload: canIncludeAssigned,
                  caseCount: currentDraftCases.length,
                  executionMode: isExecutionModeValid ? executionMode : null,
                  exporting: isExportingWorkbook,
                  closeMenuRef: closeFileMenuRef,
                  onDownloadPlan: () => void downloadCurrentPlan(),
                  onUploadClick: () => manifestInputRef.current?.click(),
                  resolving: isResolvingUploadedManifest,
                  slideCount: flatMap(currentDraftCases, 'slides').length,
                }}
                manifestInputRef={manifestInputRef}
                onFileChange={chooseManifestFile}
                sourceMenu={isOrganizationView ? selectSlidesControl : undefined}
                uploadDisabled={!canIncludeAssigned || isResolvingUploadedManifest}
              />
              <CaseManifestFileStatus
                canMatchContext={Boolean(
                  activeWorkbookContext?.result.status === 'valid' &&
                    ((activeWorkbookContext.result.context.studyId === studyId &&
                      activeWorkbookContext.result.context.labId === labId) ||
                      (destinationLookup.data && !destinationLookup.isFetching && !destinationLookup.isError))
                )}
                contextFeedback={activeWorkbookContext?.result}
                destination={destinationLookup.data}
                destinationError={
                  foreignContext && destinationLookup.isError
                    ? 'The destination is unavailable or you do not have access to organize its slides.'
                    : undefined
                }
                disabled={disableInput || !canIncludeAssigned}
                downloadError={workbookExportError}
                errors={operationErrors}
                executionMode={isExecutionModeValid ? executionMode : null}
                exporting={isExportingWorkbook}
                onConfirmOverwrite={() => {
                  if (activePendingOverwrite && overwriteAccepted)
                    void importManifestFile(activePendingOverwrite.file, false, true);
                }}
                onDismiss={() => {
                  dismissFileImport();
                  if (
                    showOnlyErrors &&
                    !planErrorMessages.length &&
                    !existingCaseAssignmentResolution.missingTargetCount &&
                    !workbookExportError
                  )
                    updateManifestDialogUrlState({ showOnlyErrors: false });
                }}
                onDismissDownloadError={() => {
                  dismissWorkbookExportError();
                  if (
                    showOnlyErrors &&
                    !planErrorMessages.length &&
                    !existingCaseAssignmentResolution.missingTargetCount &&
                    !operationErrors.length
                  )
                    updateManifestDialogUrlState({ showOnlyErrors: false });
                }}
                onKeepContext={() => reconcileWorkbookContext(false)}
                onMatchContext={() => reconcileWorkbookContext(true)}
                onOverwriteAccepted={setOverwriteAccepted}
                onRetryDestination={() => void destinationLookup.refetch()}
                overwriteAccepted={Boolean(activePendingOverwrite) && overwriteAccepted}
                pendingOverwrite={Boolean(activePendingOverwrite)}
                resolving={isResolvingUploadedManifest || Boolean(foreignContext && destinationLookup.isFetching)}
              />

              <CaseManifestWorkspace
                activeSurface={activeWorkspaceSurface}
                editingPaused={isResolvingUploadedManifest}
                loadingStatusText={activeWorkspaceSurface === 'Manifest' ? null : dialogLoadingStatusText}
                directAddSearchProps={{
                  availablePendingSlides: allLabPendingSlides || [],
                  canSearchAcrossLabs: canAddSlidesAcrossLabs,
                  onSelectedSlidesChange: setSelectedDirectSlides,
                  selectedSlides: selectedDirectSlides,
                  selectionResetKey: directSearchResetKey,
                  targetStudyId: studyId,
                }}
                notIngestedSlides={[]}
                sourcePanelProps={sourcePanelProps}
                planTableProps={{
                  labId,
                  editableCaseIds: editableManifestCaseIds,
                  editableSlideIds: editableManifestSlideIds,
                  hasDraftCases: !isEmpty(currentDraftCases),
                  isCaseLabelEditable,
                  onShowOnlyErrorsChange: onShowOnlyErrorsSwitchChange,
                  onShowOnlyUnassignedSlidesChange,
                  onUseFileNameForNewCaseLabelsChange,
                  isUnassignedSlidesLoading: isPendingSlidesLoading,
                  hasUnassignedSlidesError: Boolean(studyPendingSlidesError || labPendingSlidesError),
                  showOnlyErrors,
                  showOnlyUnassignedSlides,
                  useFileNameForNewCaseLabels,
                  gridProps: {
                    casesInPage: pageCases,
                    totalSlides,
                    totalCases,
                    isLoading:
                      isManifestDataLoading ||
                      (showOnlyUnassignedSlides && isPendingSlidesLoading) ||
                      Boolean(reviewExistingCasesWhenAvailable && !isCasesError),
                    additionalSlideColumns: existingCaseAssignmentColumns,
                    onCaseDataChange,
                    onSlideDataChange,
                    applyCellValueChangedClass,
                    onBulkEditSave,
                    onPageChange,
                    onRowsPerPageChange,
                    forcePage: page,
                    forcePageSize: rowsPerPage,
                  },
                }}
                errorMessages={[]}
                onResolveDuplicateRows={
                  isEmpty(draftDuplicateGroups) ? undefined : () => setDuplicateRowsSource('plan')
                }
                warningMessages={warningMessages}
              />
            </>
          )}
        </DialogContent>
        <CaseManifestDialogFooter
          assignmentIssues={isOrganizationView ? assignmentIssues : []}
          caseListLoadingIcon={caseListLoadingIcon}
          editingPaused={isResolvingUploadedManifest}
          footerGuidance={isOrganizationView ? footerGuidance : null}
          hasResettableManifestChanges={hasResettableManifestChanges}
          hasUploadedManifest={hasUploadedManifest}
          isCasesLoading={isCasesLoading}
          isChoosingEntryIntent={isChoosingEntryIntent}
          isOrganizationView={isOrganizationView}
          isSelectingSlides={isSelectingSlides}
          onClose={onCloseDialog}
          onCreateCasesFromFileNames={
            isOrganizationView && canIncludeAssigned && slidesNeedingFileNameCases.length > 0 && !hasDuplicateRows
              ? createCasesFromFileNames
              : undefined
          }
          onLeaveSelectedSlidesOut={removeSelectedManifestSlidesFromManifest}
          onNameFromFileName={
            canNameCollidingCasesFromFileNames && !hasDuplicateRows ? nameCollidingCasesFromFileNames : undefined
          }
          onOpenOrganization={openOrganization}
          onResetChanges={resetManifestChanges}
          onReviewChanges={() => setCaseOrganizationReviewOpen(true)}
          onSelectSlidesForCasePlan={() => setSlideAdditionDialogOpen(true)}
          runDisabledReason={runDisabledReason}
          selectedSlideCount={selectedManifestSlidesCount}
          selectionActionDisabled={selectionActionDisabled}
          showSubmit={showSubmit}
          submissionLabel={submissionLabel}
        />
      </Dialog>

      {duplicateRowsSource && !isEmpty(activeDuplicateGroups) && (
        <DuplicateSlideRowsDialog
          groups={activeDuplicateGroups}
          source={duplicateRowsSource}
          onApply={applyDuplicateRowChoices}
          onCancel={() => setDuplicateRowsSource(null)}
        />
      )}

      <CaseManifestSlideAdditionDialog
        caseOptions={existingCaseOptions}
        isCasesError={isCasesError}
        isCasesLoading={isCasesLoading}
        open={slideAdditionDialogOpen}
        selectedSlideCount={selectedSlideCount}
        onClose={() => setSlideAdditionDialogOpen(false)}
        onConfirm={handleSlideAddition}
      />

      {activeDestinationConfirmation && (
        <ConfirmationModal
          title="Switch case assignment destination"
          text={`Switch to ${activeDestinationConfirmation.destination.studyName} in ${activeDestinationConfirmation.destination.labName}? Your original unsaved plan will be preserved for return. No saved assignments will change.`}
          confirmButtonProps={{ title: 'Switch destination', color: 'primary' }}
          cancelButtonProps={{ color: 'inherit' }}
          onConfirm={() => void switchManifestDestination()}
          onCancel={dismissDestinationConfirmation}
        />
      )}
      <StudyManifestInProgressDialog
        open={runningDialogOpen}
        completedBatches={completedBatchCount}
        totalBatches={totalBatches}
        onClose={onCloseDialog}
        setRunningDialogOpen={setRunningDialogOpen}
        failureMessage={assignmentFailureMessage}
        failedBatches={failedCaseBatchCount}
        overrideFailed={overrideFailed}
        failedSchedulingCount={failedSchedulingCount}
        onRetryFailedBatches={retryFailedCaseBatches}
      />
      <CaseOrganizationConfirmationDialog
        key={`${executionMode}:${studyId}`}
        open={caseOrganizationReviewOpen && !isRunDisabled}
        onClose={() => setCaseOrganizationReviewOpen(false)}
        onApplyCaseOrganization={applyCaseOrganization}
        plan={caseOrganizationPlan}
      />
    </>
  );
};

export default CaseManifestDialog;
